import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AppleLogo, GooglePlayLogo } from "@/components/common/StoreBadges";
import { useLanguage } from "@/lib/i18n";
import { appStoreUrlFor, applyPageSeo, ogDefaultFor, playStoreUrlFor } from "@/lib/seo";
import { DOWNLOAD_PATH_EN, DOWNLOAD_PATH_FR, downloadSeo } from "@/lib/download-seo";
import { DownloadHighlights } from "@/components/download/DownloadHighlights";
import { detectPlatform, type Platform } from "@/lib/stores";
import { downloadTarget, isSocialInAppBrowser, trackDownloadEvent } from "@/lib/download";
import type { StoreName } from "@/lib/analytics";

/**
 * /download and /en/download: indexable page with a short presentation and a
 * device-specific store button. No automatic redirect (ads and answer-engine
 * crawlers must land on a readable page). The /ios and /android short links
 * still redirect.
 */
export default function DownloadPage() {
  const { language } = useLanguage();
  const location = useLocation();
  const [params] = useSearchParams();
  // Match the prerendered HTML on hydration; both links are usable even before
  // effects run. Device-specific emphasis is resolved on mount, not by a timer.
  const [device, setDevice] = useState<{ platform: Platform; inApp: boolean } | null>(null);
  const visited = useRef<string | null>(null);
  // Resolved after mount only (query included): the prerendered HTML has no
  // query string, so choosing earlier would break hydration.
  const target = device ? downloadTarget(params.get("store"), device.platform) : null;
  const appStoreUrl = appStoreUrlFor(language);
  const playStoreUrl = playStoreUrlFor(language);
  const url = target === "google_play" ? playStoreUrl : target === "app_store" ? appStoreUrl : null;
  const isFr = language === "fr";
  const pagePath = location.pathname + location.search;

  useEffect(() => {
    setDevice({ platform: detectPlatform(), inApp: isSocialInAppBrowser(navigator.userAgent || "") });
  }, []);

  useEffect(() => {
    const seo = downloadSeo(language);
    applyPageSeo({
      lang: language, title: seo.title, description: seo.description, path: seo.path,
      frPath: DOWNLOAD_PATH_FR, enPath: DOWNLOAD_PATH_EN, ogImage: ogDefaultFor(language),
    });
  }, [language]);

  useEffect(() => {
    if (!device || visited.current === location.key) return;
    visited.current = location.key;
    trackDownloadEvent("download_page_visit", {
      page_path: pagePath, language, platform: device.platform, destination: target ?? "none",
    });
  }, [device, language, location.key, pagePath, target]);

  const manualClick = (store: StoreName, linkType: "primary" | "fallback") => {
    trackDownloadEvent("download_manual_click", {
      page_path: pagePath, language, platform: device?.platform ?? detectPlatform(),
      destination: store, link_type: linkType,
    });
    // Do not preventDefault, await analytics or window.open on click:
    // the native same-tab anchor preserves the browser's user gesture.
  };

  return (
    <main className="min-h-screen bg-background px-5 pb-64 pt-8 text-center sm:pt-16">
      <div className="mx-auto flex max-w-md flex-col items-center">
        <Link to={isFr ? "/" : "/en"} className="mb-6">
          <img src="/logo-test-myreceipt-17.png" alt="ReFound" className="h-7 w-auto" />
        </Link>
        <h1 className="text-2xl font-semibold tracking-tight text-foreground sm:text-3xl">
          {isFr ? "Téléchargez ReFound" : "Download ReFound"}
        </h1>
        <p className="mt-3 text-base leading-relaxed text-muted-foreground">
          {isFr
            ? "L’application canadienne qui transforme vos reçus en preuves d’achat claires pour vos retours et garanties. Gratuite sur iPhone et Android."
            : "The Canadian app that turns your receipts into clear proof of purchase for returns and warranties. Free on iPhone and Android."}
        </p>
        {target && url ? (
          <>
            <Button asChild size="lg" className="mt-6 h-auto min-h-14 w-full whitespace-normal px-4 py-3 text-base">
              <a href={url} target="_self" data-testid="download-primary" onClick={() => manualClick(target, "primary")}>
                <Download aria-hidden="true" />
                {target === "google_play"
                  ? isFr ? "Télécharger sur Google Play" : "Download on Google Play"
                  : isFr ? "Télécharger sur l’App Store" : "Download on the App Store"}
              </a>
            </Button>
          </>
        ) : null}
        <section className="mt-6 w-full" aria-label={isFr ? "Choisir une boutique" : "Choose a store"}>
          <p className="mb-3 text-sm text-muted-foreground">
            {isFr ? "Ou choisissez votre boutique :" : "Or choose your store:"}
          </p>
          <div className="grid grid-cols-2 gap-3">
            <Button asChild variant="outline" className="h-12 px-2">
              <a href={appStoreUrl} target="_self" data-testid="download-app-store" onClick={() => manualClick("app_store", "fallback")}>
                <AppleLogo className="h-4 w-4" />App Store
              </a>
            </Button>
            <Button asChild variant="outline" className="h-12 px-2">
              <a href={playStoreUrl} target="_self" data-testid="download-google-play" onClick={() => manualClick("google_play", "fallback")}>
                <GooglePlayLogo />Google Play
              </a>
            </Button>
          </div>
        </section>
        <DownloadHighlights />
        <p data-testid="download-browser-help" className="mt-5 text-sm leading-relaxed text-muted-foreground">
          {device?.inApp
            ? isFr ? "Dans Facebook ou Instagram, si la boutique ne s’ouvre pas, utilisez le menu du navigateur pour ouvrir cette page dans Safari (iPhone) ou Chrome (Android), puis réessayez." : "In Facebook or Instagram, if the store doesn’t open, use the browser menu to open this page in Safari (iPhone) or Chrome (Android), then try again."
            : isFr ? "La boutique ne s’ouvre pas ? Ouvrez cette page dans Safari ou Chrome depuis le menu de votre navigateur, puis réessayez." : "Store not opening? Open this page in Safari or Chrome from your browser’s menu, then try again."}
        </p>
      </div>
    </main>
  );
}
